import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react"
import { ArrowUp, Square, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import { Textarea } from "../Textarea/Textarea"

export type ChatInputPrimaryAction = "send" | "stop" | "cancel"

export interface ChatInputHandle {
  focus: () => void
  /** Seed the composer with a ready-to-send text — only when it is empty. */
  prefillIfEmpty: (text: string) => void
}

export interface ChatInputProps {
  id?: string
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  onSubmit?: (value: string) => void
  onStop?: () => void
  onCancel?: () => void
  primaryAction?: ChatInputPrimaryAction
  placeholder?: string
  disabled?: boolean
  isMobileLayout?: boolean
  bannerSlot?: ReactNode
  editingSlot?: ReactNode
  contextPillSlot?: ReactNode
  /** Chrome-row controls that sit left of the send button (e.g. the effort selector). */
  leadingChromeSlot?: ReactNode
  overlaySlot?: ReactNode
  trailingChromeSlot?: ReactNode
  className?: string
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  (
    {
      id,
      value: controlled,
      defaultValue = "",
      onChange,
      onSubmit,
      onStop,
      onCancel,
      primaryAction = "send",
      placeholder = "Ask anything…",
      disabled = false,
      isMobileLayout = false,
      bannerSlot,
      editingSlot,
      contextPillSlot,
      leadingChromeSlot,
      overlaySlot,
      trailingChromeSlot,
      className,
    },
    ref,
  ) => {
    const [internal, setInternal] = useState(defaultValue)
    const value = controlled ?? internal
    const setValue = useCallback(
      (next: string) => {
        if (controlled === undefined) setInternal(next)
        onChange?.(next)
      },
      [controlled, onChange],
    )

    const textareaRef = useRef<HTMLTextAreaElement | null>(null)

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
      prefillIfEmpty: (text: string) => {
        if (value.trim().length === 0) setValue(text)
      },
    }))

    useEffect(() => {
      if (!isMobileLayout) return
      const t = textareaRef.current
      if (!t) return
      t.style.height = "auto"
      t.style.height = Math.min(t.scrollHeight, 128) + "px"
    }, [value, isMobileLayout])

    const canSubmit =
      value.trim().length > 0 && !disabled && primaryAction === "send"

    const submit = useCallback(() => {
      const text = value.trim()
      if (!text || primaryAction !== "send") return
      onSubmit?.(text)
      setValue("")
    }, [value, primaryAction, onSubmit, setValue])

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault()
          submit()
        } else if (e.key === "Escape") {
          if (primaryAction === "stop") {
            e.preventDefault()
            onStop?.()
          }
        }
      },
      [submit, primaryAction, onStop],
    )

    const actionButton = (() => {
      if (primaryAction === "cancel") {
        return (
          <Button
            type="button"
            size="icon-sm"
            variant="destructive"
            onClick={onCancel}
            aria-label="Cancel"
          >
            <X />
          </Button>
        )
      }
      if (primaryAction === "stop") {
        return (
          <Button
            type="button"
            size="icon-sm"
            variant="secondary"
            onClick={onStop}
            aria-label="Stop"
          >
            <Square className="fill-current" />
          </Button>
        )
      }
      return (
        <Button
          type="button"
          size="icon-sm"
          disabled={!canSubmit}
          onClick={submit}
          aria-label="Send message"
        >
          <ArrowUp />
        </Button>
      )
    })()

    if (overlaySlot) {
      return (
        <div id={id} className={cn("shrink-0 border-t border-border p-2 bg-background", className)}>
          {overlaySlot}
        </div>
      )
    }

    return (
      <div id={id} className={cn("shrink-0 border-t border-border p-2 bg-background", className)}>
        {bannerSlot}
        <div
          className={cn(
            "relative flex flex-col rounded-lg border border-border bg-card overflow-hidden",
            "transition-colors focus-within:border-ring/60",
          )}
        >
          {editingSlot}
          {isMobileLayout ? (
            <div className="flex items-end gap-1 pl-3 pr-2 pt-2 pb-1.5">
              <Textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                placeholder={placeholder}
                disabled={disabled}
                className={cn(
                  "flex-1 min-h-9 max-h-32 resize-none py-1.5 px-2 text-body leading-5",
                  "border-0 bg-transparent dark:bg-transparent shadow-none",
                  "focus-visible:ring-0 focus-visible:outline-none",
                  disabled && "opacity-60 cursor-not-allowed",
                )}
              />
              {actionButton}
            </div>
          ) : (
            <Textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(
                "min-h-20 max-h-32 resize-none pt-2.5 pb-2 px-3 text-body leading-5",
                "border-0 bg-transparent dark:bg-transparent shadow-none",
                "focus-visible:ring-0 focus-visible:outline-none",
                disabled && "opacity-60 cursor-not-allowed",
              )}
            />
          )}
          <div className="flex items-center justify-between gap-2 shrink-0 pl-3 pr-2 py-1 border-t border-border/40 bg-muted/30">
            {contextPillSlot ?? <div />}
            <div className="flex items-center gap-1 shrink-0">
              {leadingChromeSlot}
              {isMobileLayout ? trailingChromeSlot ?? null : actionButton}
            </div>
          </div>
        </div>
      </div>
    )
  },
)
ChatInput.displayName = "ChatInput"
